/**
 * The playback time window drawn on a map timeline as a brush-looking band, so the user can see which
 * slice of the day the map is showing — and change it there instead of on the PlayPause button:
 *
 * - drag the LEFT edge: the playhead stays put, the window grows / shrinks;
 * - drag the RIGHT edge: the left edge stays put, the playhead follows the pointer;
 * - drag the BODY: the window keeps its length and slides, so the map track slides with it.
 *
 * The band draws the same trailing slice the historical track layers draw: hidden while a brush, a cut,
 * Full, or an unset playhead suspends the window (`shouldApplyPlaybackTimeWindow`). It is locked while
 * playing. Historical maps only — the live timeline is a different component and is not touched.
 */
import * as d3 from "d3";

import {
  isPlaying,
  isUnsetPlaybackTime,
  liveMode,
  releaseTimeControl,
  requestTimeControl,
  selectedTime,
  setIsManualTimeChange,
  setSelectedTime,
  setTimeWindow,
  shouldApplyPlaybackTimeWindow,
  timeWindow,
} from "../../../../store/playbackStore";

const MS_PER_MIN = 60 * 1000;
/** Smallest window the band can be dragged to — the shortest PlayPause option. */
export const TIME_WINDOW_BAND_MIN_MINUTES = 0.5;
const EDGE_HIT_PX = 8;
/** Playhead triangle (8 px) plus its grab margin: the right edge starts below it. */
const PLAYHEAD_TRIANGLE_CLEAR_PX = 12;
const CLICK_SLOP_PX = 3;

/** Window length in whole minutes (half a minute below one), within [0.5, maxMinutes]. */
export function snapWindowMinutes(minutes: number, maxMinutes: number): number {
  const cap = Math.max(TIME_WINDOW_BAND_MIN_MINUTES, maxMinutes);
  const snapped = minutes < 1 ? Math.round(minutes * 2) / 2 : Math.round(minutes);
  return Math.min(cap, Math.max(TIME_WINDOW_BAND_MIN_MINUTES, snapped));
}

export interface BandDragResult {
  /** Playhead (right edge of the trailing window), ms. */
  tMs: number;
  minutes: number;
}

/** Left edge dragged to `pointerMs`: the playhead stays, the window is what lies between them. */
export function resizeBandFromLeft(tMs: number, pointerMs: number, maxMinutes: number): BandDragResult {
  return { tMs, minutes: snapWindowMinutes((tMs - pointerMs) / MS_PER_MIN, maxMinutes) };
}

/**
 * Right edge dragged to `pointerMs`: the left edge stays and the playhead follows the pointer. The playhead
 * lands on the snapped length, so the window stays a whole number of minutes.
 */
export function resizeBandFromRight(
  leftMs: number,
  pointerMs: number,
  domain: [number, number],
  maxMinutes: number,
): BandDragResult {
  const pointer = Math.min(domain[1], pointerMs);
  const minutes = snapWindowMinutes((pointer - leftMs) / MS_PER_MIN, maxMinutes);
  return { tMs: Math.min(domain[1], leftMs + minutes * MS_PER_MIN), minutes };
}

/** Body dragged by `deltaMs`: same length, playhead moved, kept on the timeline. */
export function panBand(startTMs: number, deltaMs: number, domain: [number, number]): number {
  return Math.min(domain[1], Math.max(domain[0], startTMs + deltaMs));
}

/** Trailing `[t − N, t]` when the historical tracks are actually windowed; otherwise the band stays hidden. */
export function timelineWindowBounds(refTimeMs: number): { startMs: number; endMs: number } | null {
  if (!Number.isFinite(refTimeMs) || isUnsetPlaybackTime(new Date(refTimeMs))) {return null;}
  if (!shouldApplyPlaybackTimeWindow()) {return null;}
  const minutes = Number(timeWindow());
  if (!Number.isFinite(minutes) || minutes <= 0) {return null;}
  return { startMs: refTimeMs - minutes * MS_PER_MIN, endMs: refTimeMs };
}

export interface TimeWindowBandOptions {
  chartHeight: number;
  /** Plot top in the band's coordinate space (the fleet timeline draws in root-svg coordinates). */
  top?: number;
  /** The timeline's current x scale (read at drag time, so a redraw never leaves a stale one). */
  getXScale: () => d3.ScaleTime<number, number> | null | undefined;
  /** Playback-store component that owns the drag. Fleet timelines use `multimaptimeseries`. */
  timeControl?: string;
  /** Called after a drag moves the playhead (the parent's stable-time hook, if it has one). */
  onTimeChange?: (time: Date) => void;
}

export interface TimeWindowBandHandle {
  /** Re-read the window and the playhead and redraw. Reactive reads happen here — call it from an effect. */
  update: () => void;
  destroy: () => void;
}

type GSelection = d3.Selection<SVGGElement, unknown, null, undefined>;
type PlotEl = SVGGElement | SVGSVGElement;

/**
 * Mount the band into the timeline's plot group (or the fleet timeline's root svg). Inserted under
 * `g.playhead-group` when that already exists, so the playhead stays on top and grabbable; otherwise
 * appended (append the playhead after it).
 */
export function mountTimeWindowBand<P extends d3.BaseType, PD>(
  plotSelection: d3.Selection<PlotEl, unknown, P, PD>,
  opts: TimeWindowBandOptions,
): TimeWindowBandHandle {
  const timeControl = opts.timeControl ?? "maptimeseries";
  const plotNode = plotSelection.node();
  const plot = d3.select(plotNode as PlotEl);
  const top = opts.top ?? 0;
  const group = (
    plot.select<SVGGElement>("g.playhead-group").empty()
      ? plot.append("g")
      : plot.insert("g", "g.playhead-group")
  ).attr("class", "time-window-band") as GSelection;
  // The right edge IS the playhead (the window trails it), and the playhead's grab area sits on top of the
  // band — so the right edge lives in its own layer that update() keeps ABOVE the playhead, starting below
  // the playhead triangle: the triangle still drags the playhead, the rest of the edge resizes the window.
  const rightGroup = plot.append("g").attr("class", "time-window-band time-window-band-right") as GSelection;
  const groups = d3.selectAll([group.node() as SVGGElement, rightGroup.node() as SVGGElement]);
  groups.style("display", "none");

  const body = group.append("rect")
    .attr("class", "time-window-band-body")
    .attr("y", top)
    .attr("height", opts.chartHeight);
  const leftEdge = group.append("rect")
    .attr("class", "time-window-band-edge time-window-band-edge-left")
    .attr("y", top)
    .attr("width", EDGE_HIT_PX)
    .attr("height", opts.chartHeight);
  const rightEdge = rightGroup.append("rect")
    .attr("class", "time-window-band-edge time-window-band-edge-right")
    .attr("y", top + PLAYHEAD_TRIANGLE_CLEAR_PX)
    .attr("width", EDGE_HIT_PX)
    .attr("height", Math.max(0, opts.chartHeight - PLAYHEAD_TRIANGLE_CLEAR_PX));

  /** What the band last drew: the drags start from it, not from a store read mid-gesture. */
  let shown: { startMs: number; endMs: number } | null = null;
  let dragging = false;

  const draw = (startMs: number, endMs: number, xScale: d3.ScaleTime<number, number>) => {
    const [r0, r1] = xScale.range();
    const x0 = Math.max(r0, xScale(startMs));
    const x1 = Math.min(r1, xScale(endMs));
    if (!Number.isFinite(x0) || !Number.isFinite(x1) || x1 <= x0) {
      groups.style("display", "none");
      return;
    }
    groups.style("display", null);
    body.attr("x", x0).attr("width", x1 - x0);
    leftEdge.attr("x", x0 - EDGE_HIT_PX / 2);
    rightEdge.attr("x", x1 - EDGE_HIT_PX / 2);
  };

  const update = () => {
    const t = selectedTime();
    const playing = isPlaying();
    const live = liveMode();
    const xScale = opts.getXScale();
    // Read the window even while a gesture is drawing its own frames, so the effect keeps its subscription.
    const bounds = !live && xScale && t ? timelineWindowBounds(t.getTime()) : null;
    if (dragging) {return;}
    if (!bounds || !xScale) {
      shown = null;
      groups.style("display", "none");
      return;
    }
    shown = { startMs: bounds.startMs, endMs: bounds.endMs };
    // While playing the clock owns the playhead.
    groups.classed("time-window-band-locked", playing);
    // Anything appended after mount (the playhead, fleet series) would cover the right edge: keep its layer last.
    if (rightGroup.node()?.nextSibling) {rightGroup.raise();}
    draw(bounds.startMs, bounds.endMs, xScale);
  };

  const domainMs = (xScale: d3.ScaleTime<number, number>): [number, number] => {
    const [d0, d1] = xScale.domain();
    return [d0.getTime(), d1.getTime()];
  };

  const canDrag = () => !!shown && !group.classed("time-window-band-locked") && !!opts.getXScale();

  const pointerMs = (event: d3.D3DragEvent<SVGRectElement, unknown, unknown>, xScale: d3.ScaleTime<number, number>) => {
    const [px] = d3.pointer(event.sourceEvent, plotNode);
    const [r0, r1] = xScale.range();
    return { px, ms: xScale.invert(Math.max(r0, Math.min(r1, px))).getTime() };
  };

  const applyTime = (tMs: number) => {
    const time = new Date(tMs);
    setIsManualTimeChange(true);
    setSelectedTime(time, timeControl);
    opts.onTimeChange?.(time);
  };

  const beginGesture = (): boolean => {
    if (!canDrag() || !requestTimeControl(timeControl)) {return false;}
    dragging = true;
    groups.classed("dragging", true);
    return true;
  };

  const endGesture = () => {
    dragging = false;
    groups.classed("dragging", false);
    setTimeout(() => releaseTimeControl(timeControl), 100);
    update();
  };

  const edgeDrag = (side: "left" | "right") => {
    let active = false;
    let anchor = { startMs: 0, endMs: 0 };
    let lastMinutes = NaN;
    return d3.drag<SVGRectElement, unknown>()
      .on("start", (event) => {
        event.sourceEvent?.stopPropagation?.();
        active = beginGesture();
        lastMinutes = NaN;
        if (active && shown) {anchor = { ...shown };}
      })
      .on("drag", (event) => {
        const xScale = opts.getXScale();
        if (!active || !xScale) {return;}
        const domain = domainMs(xScale);
        const maxMinutes = (domain[1] - domain[0]) / MS_PER_MIN;
        const { ms } = pointerMs(event, xScale);
        const next = side === "left"
          ? resizeBandFromLeft(anchor.endMs, ms, maxMinutes)
          : resizeBandFromRight(anchor.startMs, ms, domain, maxMinutes);
        // Snapped to whole minutes, so most moves change nothing: write (and cross-window sync) only on a step.
        if (next.minutes !== lastMinutes) {
          lastMinutes = next.minutes;
          setTimeWindow(next.minutes);
          if (side === "right") {applyTime(next.tMs);}
        }
        draw(next.tMs - next.minutes * MS_PER_MIN, next.tMs, xScale);
      })
      .on("end", () => {
        if (active) {endGesture();}
        active = false;
      });
  };

  let bodyStart = { px: 0, tMs: 0, minutes: 0 };
  let bodyMoved = false;
  let bodyActive = false;
  const bodyDrag = d3.drag<SVGRectElement, unknown>()
    .on("start", (event) => {
      event.sourceEvent?.stopPropagation?.();
      const xScale = opts.getXScale();
      bodyActive = !!xScale && beginGesture();
      if (!bodyActive || !xScale || !shown) {return;}
      bodyMoved = false;
      bodyStart = {
        px: pointerMs(event, xScale).px,
        tMs: shown.endMs,
        minutes: (shown.endMs - shown.startMs) / MS_PER_MIN,
      };
    })
    .on("drag", (event) => {
      const xScale = opts.getXScale();
      if (!bodyActive || !xScale) {return;}
      const { px } = pointerMs(event, xScale);
      if (!bodyMoved && Math.abs(px - bodyStart.px) <= CLICK_SLOP_PX) {return;}
      bodyMoved = true;
      const deltaMs = xScale.invert(px).getTime() - xScale.invert(bodyStart.px).getTime();
      const tMs = panBand(bodyStart.tMs, deltaMs, domainMs(xScale));
      applyTime(tMs);
      draw(tMs - bodyStart.minutes * MS_PER_MIN, tMs, xScale);
    })
    .on("end", (event) => {
      const xScale = opts.getXScale();
      // A press without a move is a click inside the band: seek there, as a click anywhere else does.
      if (bodyActive && !bodyMoved && xScale) {
        applyTime(pointerMs(event, xScale).ms);
      }
      if (bodyActive) {endGesture();}
      bodyActive = false;
    });

  body.call(bodyDrag);
  leftEdge.call(edgeDrag("left"));
  rightEdge.call(edgeDrag("right"));

  return {
    update,
    destroy: () => {
      if (dragging) {
        dragging = false;
        releaseTimeControl(timeControl);
      }
      body.on(".drag", null);
      leftEdge.on(".drag", null);
      rightEdge.on(".drag", null);
      groups.remove();
    },
  };
}
